import { type FC, Fragment, ReactNode, useCallback, useState } from "react";
import { twMerge } from "tailwind-merge";
import { useBoolean } from "usehooks-ts";

import { useCanDeleteActions } from "../../../../utils/delete";

import { ManageSectionPagesButton } from "./pages/ManageSectionPagesButton";

import { serverIngestTopic } from "api/api-server-ingestion";
import { HoverableEditField } from "components/hover-widgets/EditableField";
import { HoverableToolbar } from "components/hover-widgets/HoverableToolbar";
import { TopicSidebarOptionsMenu } from "components/menus/TopicSidebarOptionsMenu";
import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { ConfirmDeletionDialog } from "mds/components/ConfirmDeletionDialog";
import { DragAndDrop } from "mds/components/DragAndDrop";
import { FileUploadField } from "mds/components/FileUploadField";
import { IconText } from "mds/components/IconText";
import { ListItem } from "mds/components/ListItem";
import { TextAddButton } from "mds/components/TextAddButton";
import { Tooltip } from "mds/components/Tooltip";
import { useOrdered } from "mds/hooks/use-ordered";
import { EyeNotVisibleIcon, PageListCheckIcon, PageListIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import {
  TopicWithPagesType,
  selectCurrentSectionId,
  selectCurrentSectionIsTemplate,
  selectCurrentUserIsMPAdmin,
  selectIsEditModeEnabled,
  selectVisibleTopicsWithPages,
} from "store/selectors";
import { useListSelector } from "store/store-hooks";
import { toastLocalizedOperationCatcher, toastSuccessMessage } from "utils/alerts";
import { BUTTON_EVENT_CATEGORY, trackEvent } from "utils/amplitude";
import { useDnDItemOrdering, useLocalCopy } from "utils/collections";
import { PAGE_LIST_CUTOFF_LENGTH, changeReleaseStatusForPages } from "utils/page";
import { Link } from "utils/routing";
import { onTitleChanged } from "utils/store-utils";
import { useTopicActions } from "utils/topic";
import { pageUrl } from "utils/urls";

const ListItemTooltip: FC<{ children: ReactNode }> = ({ children }) => (
  <Tooltip text={t("content_view.unreleased_topic_tooltip")} wrapperClassName="w-full">
    {children}
  </Tooltip>
);

// All the prop passing is a bit annyoing, but this was the easiest way to make sure
// every topic can efficiently query it's own release status from the store
const TopicListItem: FC<{
  topic: TopicWithPagesType;
  index: number;
  lastItem: boolean;
  isInTemplateSection: boolean;
  onMove: ReturnType<typeof useDnDItemOrdering>;
}> = ({ topic, lastItem, index, isInTemplateSection, onMove }) => {
  const { canAuthorSection } = useCapabilities();
  const { copyTopic, deleteTopic, dropTopic, saveTopic } = useTopicActions();
  const [isEditingTitle, setIsEditingTitle] = useState<boolean>(false);
  const { canDeleteTopic } = useCanDeleteActions(topic.id);
  const currentSectionId = useAppSelector(selectCurrentSectionId);
  const isEditModeEnabled = useAppSelector(selectIsEditModeEnabled);
  const {
    value: confirmationDialogIsOpen,
    setTrue: openConfirmationDialog,
    setFalse: closeConfirmationDialog,
  } = useBoolean(false);
  // Note: These values have been updated in the selector and should be accurate
  const topicHasPages = topic.released_page_count + topic.unreleased_page_count > 0;
  const topicIsUnreleased = topic.released_page_count === 0 && !isInTemplateSection;

  const onDelete = useCallback(() => {
    if (topicHasPages) {
      openConfirmationDialog();
    } else {
      trackEvent("Topic - Delete", {
        ...BUTTON_EVENT_CATEGORY,
      });
      deleteTopic(topic);
    }
  }, [topic, deleteTopic, openConfirmationDialog, topicHasPages]);

  const menuProps = {
    onDelete,
    isDeleteDisabled: !canDeleteTopic,
    deleteTooltipText: canDeleteTopic ? undefined : t("tooltip.delete_topic_has_assessments"),
    onDuplicate: () => {
      trackEvent("Topic - Duplicate", {
        ...BUTTON_EVENT_CATEGORY,
        context: "within_section",
      });
      copyTopic(topic.id, [topic.section_id]);
    },
    onCopyToSection: (ids: string[]) => {
      trackEvent("Topic - Copy to section", {
        ...BUTTON_EVENT_CATEGORY,
        context: "to_section",
      });
      copyTopic(topic.id, ids);
    },
    onChangeStatus: () => changeReleaseStatusForPages(topic.pages),
    onChangeCompletedAt: () => {
      if (topic.completed_at) {
        trackEvent("Topic - Undo mark as complete", {
          ...BUTTON_EVENT_CATEGORY,
        });
        saveTopic({ id: topic.id, completed_at: null });
      } else {
        trackEvent("Topic - Mark as complete", {
          ...BUTTON_EVENT_CATEGORY,
        });
        saveTopic({
          id: topic.id,
          completed_at: new Date().toJSON(),
        });
      }
    },
  };

  if (!canAuthorSection && topicIsUnreleased) {
    return null;
  }

  const TooltipWrapper = topicIsUnreleased ? ListItemTooltip : Fragment;
  const itemStatusIcon = topicIsUnreleased ? (
    <EyeNotVisibleIcon />
  ) : topic.completed_at ? (
    <PageListCheckIcon />
  ) : (
    <PageListIcon />
  );

  return (
    <>
      {
        // TODO: This DragAndDrop -> ListItem -> HoverableToolbar -> Icon+EditableField needs
        // to be abstracted so we don't have style drift between all the activity card types,
        // and other list types (like TopicList, CoursePageList, etc). We should instead express
        // any style differences as part of the ListItem MDS styles using the `kind` property.
      }
      <DragAndDrop
        className="isolate min-w-12"
        draggable={canAuthorSection && isEditModeEnabled && !isEditingTitle}
        dragItemKind="topic"
        dropContainerId="topic"
        dropIndex={index}
        droppable={canAuthorSection && isEditModeEnabled}
        item={topic}
        key={topic.id}
        onDraggedItemHover={onMove}
        onDropItem={dropTopic}
      >
        <ListItem
          className="justify-between"
          eventName="Course - Select topic"
          eventProperties={{ topic: topic.title }}
          kind="primary"
          label={topic.title}
          lastItem={lastItem}
          to={currentSectionId ? pageUrl(currentSectionId, "", { topicId: topic.id }) : ""}
        >
          <TooltipWrapper>
            <div className="flex w-full min-w-12 flex-row items-start justify-between gap-2 py-1.5">
              <HoverableToolbar
                buttonSize="s"
                className="w-full min-w-12"
                contentClassName="bg-black-tint-97"
                eventPrefix="Topic item list - "
                readOnly={!canAuthorSection}
                reference="topic"
                alwaysShowAllChildren
                disableUnlessEditMode
                showEditButton
                uncentered
                onEditModeChange={setIsEditingTitle}
              >
                <div
                  className={twMerge(
                    "flex w-full min-w-12 items-start gap-2",
                    topicIsUnreleased && "text-black-tint-55",
                  )}
                >
                  <div className="my-1 flex min-h-6 items-center gap-2">{itemStatusIcon}</div>

                  <HoverableEditField
                    className="editable-field min-w-12"
                    label={t("glossary.topic_title")}
                    textSize="m"
                    value={topic.title}
                    disableUnlessEditMode
                    onValueChanged={(newTitle) =>
                      onTitleChanged("topics", topic.id, newTitle, {
                        eventName: "Topic - Rename",
                        eventProperties: { topic: newTitle },
                      })
                    }
                  />
                </div>

                {canAuthorSection && (
                  <TopicSidebarOptionsMenu
                    completedAt={topic.completed_at}
                    direction="left"
                    isInTemplateSection={isInTemplateSection}
                    optionsMenuActions={menuProps}
                    releasedCount={topic.released_page_count}
                    unreleasedCount={topic.unreleased_page_count}
                  />
                )}
              </HoverableToolbar>

              {!topicIsUnreleased &&
                canAuthorSection &&
                !isInTemplateSection &&
                topic.unreleased_page_count > 0 && (
                  <Tooltip text={t("content_view.unreleased_page_count_tooltip")}>
                    <IconText
                      className="mt-1 gap-1 font-medium text-black-tint-70"
                      iconEnd={<EyeNotVisibleIcon />}
                      text={topic.unreleased_page_count.toString()}
                    />
                  </Tooltip>
                )}
            </div>
          </TooltipWrapper>
        </ListItem>
      </DragAndDrop>

      <ConfirmDeletionDialog
        glossaryKey="topic"
        instanceName={topic.title}
        open={confirmationDialogIsOpen}
        onClose={closeConfirmationDialog}
        onDelete={() => {
          trackEvent("Topic - Delete", {
            ...BUTTON_EVENT_CATEGORY,
            context: "confirmed",
          });
          return deleteTopic(topic);
        }}
      />
    </>
  );
};

type TopicListProps = {
  shouldTruncateList?: boolean;
};

export const TopicList: FC<TopicListProps> = ({ shouldTruncateList }) => {
  const { canAuthorSection } = useCapabilities();
  const sectionTopics = useListSelector(selectVisibleTopicsWithPages);
  const sectionId = useAppSelector(selectCurrentSectionId);
  const isInTemplateSection = useAppSelector(selectCurrentSectionIsTemplate);
  const isMPAdmin = useAppSelector(selectCurrentUserIsMPAdmin);
  const [ingestionInputIsOpen, setIngestionInputIsOpen] = useState(false);

  const sortedTopics = useOrdered(sectionTopics);
  const [topics, setTopics] = useLocalCopy<TopicWithPagesType[]>(sortedTopics);
  const { createTopic } = useTopicActions();

  const moveTopicsLocally = useDnDItemOrdering(topics, setTopics);

  const shouldShowOverflowMenu = shouldTruncateList && topics.length > PAGE_LIST_CUTOFF_LENGTH;
  const visibleTopics = !shouldShowOverflowMenu ? topics : topics.slice(0, PAGE_LIST_CUTOFF_LENGTH);

  const createNewTopic = () => {
    if (sectionId) {
      createTopic(sectionId);
    }
  };

  // Accepted file types for ingestion; see definition of `Ingestion.ContentType`
  // in monarch/server/ingestion/models/ingestion.py.
  const ingestionAccept = [
    "application/pdf",
    "application/vnd.ms-powerpoint",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  ].join(" ");

  const ingestionOnDrop = (files: File[]) => {
    setIngestionInputIsOpen(false);
    if (sectionId) {
      serverIngestTopic(sectionId, files[0])
        .then(() => toastSuccessMessage(t("success.toasts.ingestion_success")))
        .catch(toastLocalizedOperationCatcher("ingestion_failure"));
    }
  };

  return (
    <div role="list">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-black-tint-10">{t("content_view.topics")}</h3>

        {canAuthorSection && <ManageSectionPagesButton />}
      </div>

      {!visibleTopics.length && (
        <div className="text-black-tint-55">{t(`section_view.empty_topic`)}</div>
      )}

      {visibleTopics.map((topic, index) => {
        return (
          <TopicListItem
            index={index}
            isInTemplateSection={isInTemplateSection}
            key={topic.id}
            lastItem={index === topics.length - 1}
            topic={topic}
            onMove={moveTopicsLocally}
          />
        );
      })}

      {shouldShowOverflowMenu && (
        <Button
          as={Link}
          className="my-2"
          kind="white"
          size="s"
          title={t("content_view.view_all.topic", { count: topics.length })}
          to={pageUrl(topics[0].section_id, "")}
        >
          {t(`content_view.view_all.topic`, { count: topics.length })}
        </Button>
      )}

      <div className="flex items-center gap-2">
        {canAuthorSection && (
          <TextAddButton
            className="mt-1 h-[42px]"
            eventName="Course - Add topic"
            reference="topic"
            size="s"
            hideUnlessEditMode
            underlined
            onClick={() => createNewTopic()}
          />
        )}

        {isMPAdmin && (
          <TextAddButton
            className="mt-1 h-[42px]"
            reference="ingestion"
            size="s"
            hideUnlessEditMode
            underlined
            onClick={() => setIngestionInputIsOpen(!ingestionInputIsOpen)}
          />
        )}
      </div>

      {ingestionInputIsOpen && (
        <FileUploadField accept={ingestionAccept} onDrop={ingestionOnDrop} />
      )}
    </div>
  );
};
